import { useState } from "react";
import {
  DIFFICULTY_OPTIONS,
  DIFFICULTY_LABELS,
  type Difficulty,
  multiplier as calcMult,
} from "@cslame/shared";

interface IdleProps {
  balance: number;
  onStart: (stake: number, difficulty: Difficulty) => void;
  disabled: boolean;
}

interface ActiveProps {
  multiplier: number;
  picks: number;
  stake: number;
  onCashout: () => void;
  disabled: boolean;
}

interface EndProps {
  result: { label: string; value: string; tone: "win" | "loss" };
  onReset: () => void;
}

export function IdleControls({ balance, onStart, disabled }: IdleProps) {
  const maxStake = Math.min(balance, 1000);
  const [stake, setStake] = useState(Math.min(10, Math.max(1, maxStake)));
  const [difficulty, setDifficulty] = useState<Difficulty>(5);
  const stepMult = calcMult(1, difficulty);
  const jackpot = calcMult(7, difficulty);

  return (
    <div className="p-2.5 pb-4 space-y-2">
      <div className="flex gap-2">
        <div className="hud-chip flex-1">
          <div className="text-[9px] uppercase tracking-wider text-cs-muted mb-1">
            Difficulty
          </div>
          <div className="flex gap-1.5">
            {DIFFICULTY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                className={`flex-1 py-1.5 rounded-md text-xs font-bold tracking-wider transition-colors ${
                  difficulty === d
                    ? "bg-cs-orange text-black"
                    : "bg-cs-panel border border-cs-border text-white"
                }`}
              >
                {DIFFICULTY_LABELS[d]}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-cs-muted mt-1 tabular-nums">
            <span>step ×{stepMult.toFixed(2)}</span>
            <span>jackpot ×{jackpot.toFixed(0)}</span>
          </div>
        </div>

        <div className="hud-chip flex-1">
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider text-cs-muted">Stake</span>
            <span className="text-base font-bold text-cs-gold tabular-nums">{stake}</span>
          </div>
          <input
            type="range"
            min={1}
            max={Math.max(1, maxStake)}
            step={1}
            value={stake}
            onChange={(e) => setStake(Number(e.target.value))}
            className="w-full accent-cs-orange mt-1"
          />
          <div className="flex gap-1 mt-1">
            {[10, 50, 100].map((v) => (
              <button
                key={v}
                onClick={() => setStake(Math.min(v, maxStake))}
                className="flex-1 btn-secondary py-1 text-[10px] rounded"
              >
                {v}
              </button>
            ))}
            <button
              onClick={() => setStake(Math.max(1, maxStake))}
              className="flex-1 btn-secondary py-1 text-[10px] rounded"
            >
              MAX
            </button>
          </div>
        </div>
      </div>

      <button
        disabled={disabled || stake < 1 || stake > balance}
        onClick={() => onStart(stake, difficulty)}
        className="btn-primary w-full py-2.5"
      >
        ▸ Place bet • Arm C4
      </button>
    </div>
  );
}

export function ActiveControls({
  multiplier,
  picks,
  stake,
  onCashout,
  disabled,
}: ActiveProps) {
  const projected = Math.floor(stake * multiplier);
  return (
    <div className="p-3 pb-4">
      <button
        disabled={disabled || picks < 1}
        onClick={onCashout}
        className="btn-cashout w-full flex items-center justify-between px-5 py-3"
      >
        <span>Cut Wire • Cash Out</span>
        <span className="tabular-nums">
          {projected.toLocaleString()}{" "}
          <span className="opacity-70">({multiplier.toFixed(2)}×)</span>
        </span>
      </button>
    </div>
  );
}

export function EndControls({ result, onReset }: EndProps) {
  return (
    <div className="p-3 pb-4 space-y-2">
      <div
        className={`hud-chip text-center ${
          result.tone === "win" ? "border-cs-green/60" : "border-cs-red/60"
        }`}
      >
        <div className="text-[10px] uppercase tracking-wider text-cs-muted">
          {result.label}
        </div>
        <div
          className={`text-xl font-extrabold tabular-nums ${
            result.tone === "win" ? "text-cs-green" : "text-cs-red"
          }`}
        >
          {result.value}
        </div>
      </div>
      <button onClick={onReset} className="btn-primary w-full">
        Play again
      </button>
    </div>
  );
}
